/**
 * The dial: where its numbers sit, which one is chosen, and what a key does to it.
 *
 * Geometry, a keyboard policy and an ARIA projection — none of which is a type. They were in a file
 * called `timepicker-field-types.ts`, which is what a name does when a module grows past it: the one
 * `*-types.ts` in this folder holding two hundred lines of behaviour, and the only place a reader
 * looking for the dial's rules would not think to open.
 */
import { given } from "../given.js";
import { to24Hour } from "@modyra/core/datetime";
import { isOnStep, MDY_EVERY_TIME } from "../time-granularity.js";
import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { stateClass } from "../state.js";
import { stepTimeField, timeFieldBounds } from "../time-bounds.js";
/**
 * The numbers on the face for the field being picked.
 *
 * The hours a face offers are the hours the format has: 1–12 with an AM/PM toggle beside them, or
 * 0–23 with no toggle at all. It used to always answer 1–12, so a 24-hour picker showed a face on
 * which 14:00 could not be pointed at — the value was reachable by typing and by dragging, and not
 * by the control that exists to pick it. **A time keeps its formalism on screen as well as in its
 * value**: a face that offers thirteen hours in a twelve-hour clock, or twelve in a twenty-four
 * hour one, is telling the user something untrue about what they are editing.
 */
export function timepickerDialNumbers(field, format = "12h", steps = MDY_EVERY_TIME) {
    // A face draws what the field accepts, judged by the same rule and from the same start. Measuring
    // the face from 0 while `acceptTimeField` measures from the range's own minimum is how a 12-hour
    // clock came to draw a 12 it would then refuse — the two halves of one rule, disagreeing, with
    // nothing able to notice because each is correct on its own terms.
    const { min, step } = timeFieldBounds(field, format, steps);
    const offered = (value) => isOnStep(value - min, step);
    if (field === "hour") {
        const outer = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].filter((hour) => offered(hour)).map((hour) => Object.freeze({ value: hour, label: String(hour), index: hour, ring: "outer" }));
        if (format === "12h")
            return Object.freeze(outer);
        // The 24-hour face: 1–12 outside, then 13–23 and 00 on the inner ring at the same positions.
        // `00` rather than `24`, because midnight is the hour a 24-hour clock actually names.
        const inner = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map((hour, position) => ({ hour, position })).filter(({ hour }) => offered(hour)).map(({ hour, position }) => Object.freeze({
            value: hour,
            label: String(hour).padStart(2, "0"),
            // Midnight sits at the top, where 12 sits on the outer ring.
            index: position === 0 ? 12 : position,
            ring: dialRingOf("hour", hour, "24h"),
        }));
        return Object.freeze([...outer, ...inner]);
    }
    return Object.freeze(Array.from({ length: 12 }, (_, position) => position).filter((position) => offered(position * 5)).map((position) => {
        const minute = position * 5;
        return Object.freeze({
            value: minute,
            label: String(minute).padStart(2, "0"),
            // Minute 0 is at the top of the face, which is position 12.
            index: position === 0 ? 12 : position,
            ring: "outer",
        });
    }));
}
/** Where a number sits, in degrees clockwise from the top. */
export function dialNumberAngle(number) {
    return (number.index % 12) * 30;
}
/**
 * The value a dragged pointer lands on: the nearest one the field **offers**.
 *
 * Offered is not the same set as drawn, and conflating them coarsens the dial. A minute face has
 * twelve positions and a minute field has sixty values, so an ungranulated picker draws 0, 5, 10 …
 * and still accepts every minute — the hand sits between two labels, which is what a clock does.
 * Landing only on drawn numbers would silently turn every picker in the library into a five-minute
 * one.
 *
 * What must hold instead is that a drag can only reach a value the field would accept, at any step.
 * Hours have as many values as positions, so for them the two sets coincide; minutes do not.
 *
 * A tie — an angle exactly between two offered values — goes **clockwise**, to the later of the two.
 * Arbitrary, but stated: an unstated tie-break is one the face and the keyboard can resolve
 * differently.
 *
 * Returns `null` only when the granularity offers this field nothing at all, which a validated one
 * never does.
 */
export function timepickerDialPick(angle, field, format = "12h", ring = "outer", steps = MDY_EVERY_TIME, previous) {
    const candidates = [];
    if (field === "hour") {
        // Twelve positions, and which hour each names depends on the ring — the same direction is 3
        // outside and 15 inside. The drawn numbers already carry that, so they are the candidates.
        for (const number of timepickerDialNumbers("hour", format, steps)) {
            if (number.ring === ring || format !== "24h") {
                candidates.push({ value: number.value, angle: dialNumberAngle(number), ring: number.ring });
            }
        }
        // A granularity can thin a ring to nothing — `hourStep: 7` leaves one hour on the whole outer
        // ring — and a pointer over an empty ring still has to land somewhere it can see.
        if (candidates.length === 0) {
            for (const number of timepickerDialNumbers("hour", format, steps)) {
                candidates.push({ value: number.value, angle: dialNumberAngle(number), ring: number.ring });
            }
        }
    }
    else {
        const { min, max, step } = timeFieldBounds("minute", format, steps);
        for (let minute = min; minute <= max; minute += 1) {
            if (isOnStep(minute - min, step))
                candidates.push({ value: minute, angle: minute * 6, ring: "outer" });
        }
    }
    if (candidates.length === 0)
        return null;
    const target = ((angle % 360) + 360) % 360;
    const apartFrom = (at) => Math.abs(((target - at + 540) % 360) - 180);
    // The same rule the ring answers on the other axis, and for the same reason. Twelve hours 30°
    // apart put the boundary halfway between two of them, and at a hand of 100 one degree is 1.75px
    // of arc — so a finger that trembles crosses it repeatedly and the hour changes several times
    // while the hand is, to its owner, still. Nearest-value is the right answer to *which number is
    // this* and the wrong one to *should the number change*.
    //
    // Leaving the number in hand takes passing the boundary by a quarter of the spacing, which has to
    // be a fraction of the spacing rather than a count of degrees: minutes sit 6° apart where hours
    // sit 30°, and a margin that suits one is either nothing or everything on the other.
    if (previous !== undefined) {
        const held = candidates.find((candidate) => candidate.value === previous);
        if (held && apartFrom(held.angle) <= (360 / candidates.length) * 0.75)
            return held;
    }
    let best = candidates[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of candidates) {
        const apart = apartFrom(candidate.angle);
        // `<=` rather than `<`, and the candidates run clockwise, so a tie takes the later one.
        if (apart <= bestDistance) {
            bestDistance = apart;
            best = candidate;
        }
    }
    return best;
}
/**
 * Which number on the face is the selected one.
 *
 * Minutes are shown in fives, so a draft of 07 marks the 05 that is nearest to it rather than
 * marking nothing at all.
 *
 * **In the units the face shows.** The draft holds hours as 1–12 with a period whatever the format,
 * and a 24-hour face is numbered 0–23 — so answering the draft's hour marked `2` while the user was
 * looking at `14`. The face's numbers and the face's mark have to be decided by the same rule, or
 * they will eventually disagree; this is that rule, and `timepickerDialNumbers` is its other half.
 */
export function timepickerSelectedDialValue(field, draft, format = "12h") {
    if (field === "minute")
        return (Math.round(draft.minute / 5) * 5) % 60;
    return format === "24h" ? to24Hour(draft) : draft.hour;
}
export function timepickerDialGhost(pointerAngle, pick, options = {}) {
    const at = ((pointerAngle % 360) + 360) % 360;
    const apart = Math.abs(((at - pick.angle + 540) % 360) - 180);
    const within = options.within ?? 0;
    if (!(apart > within))
        return null;
    // Three cases, because there are three. `pointerReach === 0` is a pointer at the exact centre —
    // geometry known perfectly, whose answer is a hand of no length. `undefined` is a face nobody
    // measured, whose answer is the full hand because nothing better is available. Testing `> 0` put
    // both in the first branch; testing `!== undefined` fixed that and let `NaN` through into the
    // second, which is worse than either.
    //
    // `reach` becomes `--tp-ghost-reach`, and CSS **drops** a declaration whose value does not parse
    // rather than falling back — so the property keeps whatever it had last and the hand freezes where
    // it was, which looks exactly like a hand that is tracking something.
    const hand = options.handLength ?? 0;
    const pointer = options.pointerReach;
    const measured = hand > 0 && pointer !== undefined
        ? Math.min(Math.max(pointer, 0) / hand, 1)
        : 1;
    // A number that is present without being one is not a measurement. The full hand is the wrong
    // length and a length the sheet will take, which is the failure to prefer.
    const reach = Number.isFinite(measured) ? measured : 1;
    return { angle: at, ring: options.ring ?? pick.ring, reach };
}
/**
 * How far off a number the pointer can be and still be **on** it, in degrees.
 *
 * Not half the gap to the next number. That was the first answer and it is a tautology: the pick is
 * *defined* as the nearest offered value, so the pointer is always within half a gap of it, and a
 * ghost that appears past half a gap never appears at all. Measured across three faces it was hidden
 * at every one of 360 angles, with every unit test of the parts still green.
 *
 * On the number means on the number. A digit is `MDY_TIMEPICKER_NUMBER_SIZE` wide, drawn at a radius
 * of `handLength` outside and `handLength × MDY_TIMEPICKER_INNER_RING` inside, so it subtends
 * `atan((size / 2) / radius)` either side of its own direction — about 11° out there and about 18°
 * further in, because a same-sized number covers more of a smaller circle.
 */
export function timepickerDialTolerance(ring = "outer", handLength = 0) {
    if (!(handLength > 0))
        return 0;
    const radius = ring === "inner" ? handLength * MDY_TIMEPICKER_INNER_RING : handLength;
    return (Math.atan((MDY_TIMEPICKER_NUMBER_SIZE / 2) / radius) * 180) / Math.PI;
}
/**
 * The stretches of a ring that carry no time anybody can land on.
 *
 * A face declared with `minuteStep: 15` draws four numbers, and the other 356° of the ring look
 * exactly like the four — continuous, uniform, and offering nothing. The granularity is real and
 * invisible, discoverable only by trying, which is the state a dimmed arc is meant to end.
 *
 * The arcs are the positions the granularity **took away** — the ones a face with no declaration
 * would draw and this one does not — each covered by the knob's own angular half-width, which is
 * already the tolerance for whether a pointer is on a number. Not the space between the numbers that
 * remain: a face offering every hour has gaps between its knobs and nothing unavailable in them.
 *
 * **Two widths, deliberately.** Snapping is still nearest-value: every angle resolves to some number,
 * including the ones inside these arcs. What is dimmed is where you can *land on the number you are
 * pointing at*, which is a narrower question and a display one. The next reader will want to make the
 * two agree; they are different questions and the answer is no.
 *
 * A face whose knobs already overlap has nothing to dim and answers `[]` — sixty minutes 6° apart
 * under an 11° half-width is the ordinary case, so a ring of slivers there would be noise on every
 * picker that declares nothing.
 */
export function timepickerDialUnavailableArcs(field, format = "12h", steps = MDY_EVERY_TIME, handLength = 0, ring) {
    // Asked without a ring, it answers for every ring this face has — which is a question about the
    // face, so the face answers it. Three renderers each deciding "does this one have an inner ring"
    // is three places to get a 24-hour hour face wrong.
    if (ring === undefined) {
        const rings = format === "24h" && field === "hour" ? ["outer", "inner"] : ["outer"];
        return rings.flatMap((each) => timepickerDialUnavailableArcs(field, format, steps, handLength, each));
    }
    if (!(handLength > 0))
        return [];
    const onRing = (from) => timepickerDialNumbers(field, format, from)
        .filter((number) => number.ring === ring)
        .map((number) => dialNumberAngle(number));
    // Against what the face would draw with no granularity at all, because that is what "carries no
    // selectable time" means: a position this field took away, not the ordinary space between labels.
    // A face offering every hour has gaps between its knobs and nothing unavailable in them.
    const everything = onRing(MDY_EVERY_TIME);
    const offered = new Set(onRing(steps));
    everything.sort((a, b) => a - b);
    const missing = everything.filter((angle) => !offered.has(angle));
    if (missing.length === 0)
        return [];
    const half = timepickerDialTolerance(ring, handLength);
    const gone = new Set(missing);
    const arcs = [];
    for (const angle of missing) {
        const from = angle - half;
        const to = angle + half;
        const previous = arcs[arcs.length - 1];
        // Positions that were neighbours on the full face run together, including the space between
        // them: nothing selectable lies in it either, and drawing two slices with a live-looking sliver
        // between them would say there is something there.
        const before = everything[everything.indexOf(angle) - 1];
        if (previous && before !== undefined && gone.has(before)) {
            arcs[arcs.length - 1] = { from: previous.from, to };
        }
        else {
            arcs.push({ from, to });
        }
    }
    // The seam across the top of the face, asked the same way the loop asks it. The loop cannot see it:
    // the position at 0° has no predecessor in the list, so the first arc never merges backwards.
    //
    // Asking whether the two arcs *overlap* instead is a different question with a different answer —
    // neighbours on an hour face are 30° apart under an 11.3° half-width and can never touch, so an
    // overlap test only ever fires where nothing needed joining. That left 7.4° of undimmed ring at
    // twelve o'clock, which is the most looked-at point on a clock and reads as somewhere you can land.
    const seamIsDead = missing.includes(everything[0]) && missing.includes(everything[everything.length - 1]);
    if (arcs.length > 1 && seamIsDead) {
        const first = arcs[0];
        const last = arcs.pop();
        arcs[0] = { from: last.from - 360, to: first.to };
    }
    return arcs.map((arc) => ({
        from: ((arc.from % 360) + 360) % 360,
        to: ((arc.to % 360) + 360) % 360,
        span: ((arc.to - arc.from) % 360 + 360) % 360,
        ring,
    }));
}
/**
 * Which ring a value sits on. One predicate, so the face and the hand cannot disagree about it.
 *
 * Only a 24-hour face has two rings: midnight and 13–23 go inside, at the same twelve positions as
 * 12 and 1–11.
 */
export function dialRingOf(field, value, format) {
    if (field !== "hour" || format !== "24h")
        return "outer";
    return value === 0 || value >= 13 ? "inner" : "outer";
}
/**
 * Which ring the hand is currently pointing into.
 *
 * The hand is drawn shorter for the inner ring, and that is not decoration: a granularity can put
 * two hours at one position — with `hourStep: 3` the outer 3 and the inner 15 share a direction —
 * and without the difference in length the two selections are identical on screen. A person cannot
 * tell which they chose until they read the header.
 */
export function timepickerSelectedRing(field, draft, format = "12h") {
    return dialRingOf(field, timepickerSelectedDialValue(field, draft, format), format);
}
/**
 * The keyboard on the clock face.
 *
 * A dial is a control and had none: it listened for `mousedown` and `touchstart` and nothing else,
 * so the clock could only be operated by pointer. Everything on it was reachable by *dragging* a
 * hand around a circle, which is the one gesture a keyboard cannot make and a screen reader cannot
 * describe.
 *
 * The policy, once, here:
 *
 * - **Right/Up** steps forward, **Left/Down** back. Up is forward because the hand sweeps clockwise;
 *   a keyboard user turning the hand should feel the same direction the pointer drags.
 * - **Home/End** go to the first and last value the face offers — which depends on the format, and
 *   is exactly the rule this function exists to hold in one place.
 * - **PageUp/PageDown** move minutes by the five the face is marked in, and hours by three, so a
 *   quarter of the dial is one keystroke.
 * - Every result **wraps** rather than clamping. A clock is a ring: 23 then 00, 59 then 00, 12 then
 *   1. Clamping at the end of a circle is the one behaviour a dial cannot justify.
 *
 * And the range is the format's: hours run **1–12 with a period beside them, or 0–23 with none**.
 * Never thirteen hours on a twelve-hour clock, never a stray AM on a twenty-four hour one.
 *
 * Returns `null` for a key the dial does not claim, so a host can let it through.
 */
/**
 * How far in the inner ring sits, as a fraction of the **hand's length**.
 *
 * The stylesheet draws it at `0.6` of `--tp-hand-length` and says why there; this is the same number
 * because the two have to agree. `INNER_RING_RADIUS` is the one value shared between the drawing and
 * the hit test, and a contract test holds it against the stylesheet — everything else about where
 * the rings land is measured rather than assumed.
 */
export const MDY_TIMEPICKER_INNER_RING = 0.6;
/**
 * How wide a number on the face is, in pixels.
 *
 * The stylesheet's `--tp-num-size`, which is also what `--tp-hand-length` subtracts half of to find
 * the radius the outer digits sit at. Published here for the same reason the ring fraction is: the
 * question "is the pointer on this number" is answered in the contract, and a renderer measuring the
 * box itself would be a second answer that drifts. `css-properties.spec.mjs` holds the two together.
 */
export const MDY_TIMEPICKER_NUMBER_SIZE = 40;
/**
 * How far **above** the inner ring's radius still counts as reaching for it, as a fraction of the
 * gap between the two painted radii.
 *
 * `0.5` is not a tuning. A digit box is `MDY_TIMEPICKER_NUMBER_SIZE` wide and the two rings are
 * exactly that far apart, so the boxes touch: the inner one spans 40–80 and the outer 80–120 at a
 * hand of 100. *"Il puntatore al centro tra la fine del box del carattere 9 e la fine del box del
 * carattere 21"* has one answer, the single point where they meet, and half the gap is its closed
 * form.
 *
 * It was `0.35` for a while, and that number was compensating for a measurement: `--tp-hand-length`
 * was being read back as an unresolved `calc()` and every renderer fell through to half the *face*,
 * 128 where the hand is 100. Against that circle the edge landed near 95 — a press just inside the
 * outer digits read as the inner ring — and tightening the fraction moved it back by hand. With the
 * hand measured properly the construction gives 80 on its own, and 0.35 would leave 74–80 answering
 * `outer` while the pointer is inside the inner digit's own box: the same complaint mirrored.
 *
 * A fraction rather than an expression, still, because where the edge belongs inside a *touching*
 * pair is a judgement — this one happens to have been made by the person using it.
 *
 * One-sided, and it will look asymmetric: **below the inner ring there is no other ring to belong
 * to.** The outer ring is outside the inner one, so everything beneath the inner digits is nearer
 * them than anything else on the face and has exactly one plausible answer — *"se il puntatore va
 * troppo verso il centro si riscala alla circonferenza maggiore, questo è un grossissimo errore"*.
 */
export const MDY_TIMEPICKER_RING_BAND = 0.5;
/**
 * Which ring of the face a pointer landed on.
 *
 * A 12-hour face has one ring and always answers `"outer"`. A 24-hour face has two at the same twelve
 * positions, so the angle alone cannot name the hour — asked without this, half the numbers the face
 * draws had no way to be chosen.
 *
 * `handLength` is the radius the outer digits are drawn at, which the stylesheet computes as
 * `dialSize / 2 − numSize / 2 − 8px` and publishes as `--tp-hand-length`. It is passed in rather
 * than derived because those three numbers belong to the drawing: a copy of them here is a copy that
 * drifts, and the first version of this function compared a fraction of the *hand* against a
 * fraction of the *dial radius* — two different lengths — which put the boundary 2.4px outside the
 * outer digits. Every point on the face read as `inner`, including the outer numbers themselves.
 *
 * The inner ring is a **band around where its numbers are drawn**, not everything closer than the
 * outer ones. Reading the whole disc below the midpoint as inner makes the empty middle of the face
 * — most of its area — answer with an hour whose number is nowhere near the pointer, and the hand
 * jumps short for a press the user aimed at the outer ring. The band is as wide as the gap between
 * the two painted radii, centred on the inner one, so a press has to be near the digits to claim
 * them and anything else belongs to the ring that is actually drawn out there.
 *
 * `field` is here because only the hour has two rings. A minute face draws one, and a rule that
 * answered `"inner"` for a press near the centre of it shortened the hand for a ring that does not
 * exist.
 */
/**
 * How long this face's hand is at full reach, in pixels, measured from the drawing.
 *
 * Every angle-at-a-radius on the dial is a fraction of this, so it has to come from the paint rather
 * than from a copy of the stylesheet's arithmetic in TypeScript. Two ways of reading it have already
 * been wrong:
 *
 * - **`--tp-hand-length` off the face.** A custom property resolves at *use*, so reading it back
 *   gives the token stream — `calc(256px/2 - 40px/2 - 8px)` — and the fallback beside it ran instead:
 *   half the face, 128 where the hand is 100.
 * - **The hand's own height, taken as it is.** The hand is drawn shorter when it points into the
 *   inner ring, and which ring it points into is *the answer this measurement produces*. Each state
 *   became the other's cause: on the inner ring it measured 60, whose thresholds put the same
 *   position on the outer ring, whose hand measures 100, whose thresholds put it back. It alternated
 *   on every pointer event — not a tremor, a feedback loop, and no amount of hysteresis damps it
 *   because what moves is the thresholds themselves.
 *
 * So the shortened state is divided back out. The modifier the element carries says which state it is
 * in, and `MDY_TIMEPICKER_INNER_RING` is the fraction the sheet drew it at — the same constant, held
 * against the stylesheet by `css-properties.spec.mjs`.
 *
 * One helper rather than the same line in three renderers: the identical line in three files is how
 * this arrived, and the next adapter would copy whatever it found.
 */
export function dialHandLength(face) {
    const base = MDY_WIDGET_CONTRACTS.timepicker.parts.dialHand.classes[0];
    const ghost = stateClass(base, "ghost");
    // The ghost is a hand too, and its length follows the pointer — a worse ruler than the real one.
    const hand = Array.from(face.querySelectorAll(`.${base}`)).find((el) => !el.classList.contains(ghost));
    const drawn = hand ? Number.parseFloat(getComputedStyle(hand).height) : Number.NaN;
    if (!Number.isFinite(drawn) || drawn <= 0) {
        // Nothing drawn to measure: the face's own radius is the only thing left, and a face with no
        // stylesheet has no rings for the answer to be wrong about.
        return face.getBoundingClientRect().width / 2;
    }
    return hand.classList.contains(stateClass(base, "inner")) ? drawn / MDY_TIMEPICKER_INNER_RING : drawn;
}
export function timepickerDialRing(face, clientX, clientY, format, handLength, field = "hour", previous) {
    given("timepickerDialRing", "{ width, height, left, top }", face, ["width", "height", "left", "top"]);
    if (field !== "hour" || format !== "24h")
        return "outer";
    if (!(handLength > 0))
        return "outer";
    const dx = clientX - (face.left + face.width / 2);
    const dy = clientY - (face.top + face.height / 2);
    const reach = Math.sqrt(dx * dx + dy * dy);
    const inner = handLength * MDY_TIMEPICKER_INNER_RING;
    // One edge, because there is only one place the answer can change: everything closer than the
    // inner digits is still nearer them than anything else on the face.
    const edge = inner + (handLength - inner) * MDY_TIMEPICKER_RING_BAND;
    if (previous === undefined)
        return reach <= edge ? "inner" : "outer";
    // Where the rings divide and whether to change are two questions, and one comparison was answering
    // both. A person resting a finger on the outer part of an inner number is sitting exactly on the
    // edge, and a hand is never still: measured, a 6px wander changed the ring four times, each one the
    // hand jumping its own length and the face swapping which twelve numbers it picks from.
    //
    // So leaving a ring takes reaching halfway from the edge to the *other* ring's numbers — derived
    // from where they are drawn rather than picked, and about half a digit box either side. A
    // deliberate move still costs one gesture; drift costs nothing. The first answer of a gesture has
    // no previous ring and falls through to the edge above, so where the rings divide is unchanged.
    const leaveInner = (edge + handLength) / 2;
    const leaveOuter = (edge + inner) / 2;
    if (previous === "inner")
        return reach > leaveInner ? "outer" : "inner";
    return reach < leaveOuter ? "inner" : "outer";
}
export function timepickerDialKeyIntent(key, field, format, current, steps = MDY_EVERY_TIME) {
    // Every surface of a 24-hour picker speaks 0–23 — this one, the face, the segment bounds, what a
    // typed entry is accepted as — and the working copy is canonically 1–12. The controller converts:
    // `set-hour` and `set-from-angle` take the hour in the picker's own format and derive the half of
    // the day from it. A host sends what its face shows and nothing else.
    //
    // The bounds and the stepping are `timeFieldBounds` and `stepTimeField` rather than literals: a
    // dial's arrows are the same movement as a segment's, and written twice they answered differently
    // the moment a field offered only some of its values — the keyboard walking through times the face
    // did not draw and the field would refuse.
    const { min } = timeFieldBounds(field, format, steps);
    // A page is a bigger jump of the same kind, so it is whole steps rather than a fixed distance: on
    // a quarter-hour face, five single steps is more than an hour.
    const page = field === "minute" ? 5 : 3;
    const by = (delta) => ({
        field,
        value: stepTimeField(field, format, current, delta, steps),
    });
    switch (key) {
        case "ArrowRight":
        case "ArrowUp":
            return by(1);
        case "ArrowLeft":
        case "ArrowDown":
            return by(-1);
        case "PageUp":
            return by(page);
        case "PageDown":
            return by(-page);
        case "Home":
            return { field, value: min };
        case "End":
            // The last value on offer, which is not the range's end when the step does not divide it.
            return { field, value: stepTimeField(field, format, min, -1, steps) };
        default:
            return null;
    }
}
/**
 * What a screen reader is told about the number a person is editing.
 *
 * The hour and the minute are the operable controls: they hold the value, they answer the arrows,
 * and they are what `timepickerTabOrder` walks. The dial beside them sets nothing they cannot set,
 * so it is hidden and this is where the value is announced — once, on the control that can act on it.
 *
 * `valueText` is the reason the three numbers are not enough on their own: `15` on a twelve-hour
 * face is `3 PM`, and a minute of `5` is `05`. A reader given the raw number says something the face
 * does not show.
 */
export function timepickerSegmentAria(field, format, current, period) {
    // The range stated to a reader is the range the arrows honour, so both come from the bounds door
    // rather than being written a second time here.
    const { min, max } = timeFieldBounds(field, format);
    // Three on a twelve-hour clock is three in the morning or three in the afternoon, and the number
    // alone says neither. The period sits in a control of its own beside the hour, which a reader
    // meets one stop later — by which point they have already been told a bare "3".
    const hourText = format === "12h" && period ? `${current} ${period}` : `${current} hours`;
    return {
        role: "spinbutton",
        valueMin: min,
        valueMax: max,
        valueNow: current,
        valueText: field === "minute" ? `${String(current).padStart(2, "0")} minutes` : hourText,
    };
}
/** Semantic state of a timepicker field widget. */
